import { useEffect, useMemo, useRef, type ComponentType, type MutableRefObject } from 'react';
import {
  MapContainer as LeafletMapContainer,
  Marker as LeafletMarker,
  Popup as LeafletPopup,
  TileLayer as LeafletTileLayer,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';

import { LIGHT_TILE_LAYER } from '@src/components/Maps/constants';

import { createAddressMapMarkerIcon } from './addressMapMarkerIcon';
import styles from './styles.module.less';

const MapContainer = LeafletMapContainer as unknown as ComponentType<any>;
const TileLayer = LeafletTileLayer as unknown as ComponentType<any>;
const Marker = LeafletMarker as unknown as ComponentType<any>;
const Popup = LeafletPopup as unknown as ComponentType<any>;

type TAddress = {
  latitude?: number;
  longitude?: number;
  lat?: number;
  lon?: number;
  lng?: number;
};

type TPosition = {
  lat?: number;
  lon?: number;
};

export type AddressMapMarkerVariant = 'default' | 'selected' | 'workplace' | 'mark';

export type AddressMapMarker = {
  key: string;
  latitude: number;
  longitude: number;
  variant?: AddressMapMarkerVariant;
  label?: string;
};

type TAddressMapViewerProps = {
  address?: TAddress | null;
  /** When set, renders these markers instead of (or in addition to) a single `address`. */
  markers?: AddressMapMarker[] | null;
  initialPosition?: TPosition;
  zoom?: number;
  visible?: boolean;
  customHeight?: string;
};

// Leaflet's default icon URL detection breaks under bundlers; remove it before merging explicit URLs.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MapResizer = ({
  mapRef,
  visible,
}: {
  mapRef: MutableRefObject<L.Map | null>;
  visible: boolean;
}) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  useEffect(() => {
    if (visible) {
      const firstResize = setTimeout(() => map.invalidateSize(), 100);
      const secondResize = setTimeout(() => map.invalidateSize(), 300);

      return () => {
        clearTimeout(firstResize);
        clearTimeout(secondResize);
      };
    }
    return undefined;
  }, [visible, map]);

  return null;
};

const FitMarkers = ({ markers }: { markers: Array<{ lat: number; lon: number }> }) => {
  const map = useMap();

  useEffect(() => {
    if (markers.length === 0) return;
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lon], map.getZoom(), { animate: false });
      return;
    }
    const bounds = L.latLngBounds(markers.map(marker => [marker.lat, marker.lon] as [number, number]));
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 17 });
  }, [map, markers]);

  return null;
};

const getAddressPosition = (
  address?: TAddress | null,
  initialPosition?: TPosition,
): { lat: number; lon: number; hasValidLocation: boolean } => {
  const lat = address?.latitude ?? address?.lat ?? initialPosition?.lat;
  const lon = address?.longitude ?? address?.lng ?? address?.lon ?? initialPosition?.lon;

  if (typeof lat === 'number' && typeof lon === 'number') {
    return { lat, lon, hasValidLocation: true };
  }

  return { lat: -33.4489, lon: -70.6693, hasValidLocation: false };
};

const AddressMapViewer = ({
  address = null,
  markers = null,
  initialPosition,
  zoom = 16,
  visible = true,
  customHeight = '320px',
}: TAddressMapViewerProps) => {
  const mapRef = useRef<L.Map | null>(null);
  const lastViewKeyRef = useRef<string>('');

  const icons = useMemo(
    () => ({
      default: createAddressMapMarkerIcon({ variant: 'default' }),
      selected: createAddressMapMarkerIcon({ variant: 'selected' }),
      workplace: createAddressMapMarkerIcon({ variant: 'workplace' }),
      mark: createAddressMapMarkerIcon({ variant: 'mark' }),
    }),
    [],
  );

  const resolvedMarkers = useMemo((): AddressMapMarker[] => {
    if (Array.isArray(markers) && markers.length > 0) {
      return markers.filter(
        marker =>
          Number.isFinite(marker.latitude) &&
          Number.isFinite(marker.longitude) &&
          !!marker.key,
      );
    }

    const position = getAddressPosition(address, initialPosition);
    if (!position.hasValidLocation) return [];
    return [
      {
        key: 'address',
        latitude: position.lat,
        longitude: position.lon,
        variant: 'default',
      },
    ];
  }, [
    markers,
    address?.latitude,
    address?.longitude,
    address?.lat,
    address?.lon,
    address?.lng,
    initialPosition?.lat,
    initialPosition?.lon,
  ]);

  const fallbackCenter = useMemo(() => getAddressPosition(address, initialPosition), [
    address?.latitude,
    address?.longitude,
    address?.lat,
    address?.lon,
    address?.lng,
    initialPosition?.lat,
    initialPosition?.lon,
  ]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (resolvedMarkers.length > 0) {
      return [resolvedMarkers[0].latitude, resolvedMarkers[0].longitude];
    }
    return [fallbackCenter.lat, fallbackCenter.lon];
  }, [fallbackCenter.lat, fallbackCenter.lon, resolvedMarkers]);

  const fitPoints = useMemo(
    () => resolvedMarkers.map(marker => ({ lat: marker.latitude, lon: marker.longitude })),
    [resolvedMarkers],
  );

  useEffect(() => {
    if (!mapRef.current || resolvedMarkers.length !== 1) return;

    const marker = resolvedMarkers[0];
    const nextViewKey = `${marker.latitude}|${marker.longitude}|${zoom}`;
    if (lastViewKeyRef.current === nextViewKey) return;

    mapRef.current.setView([marker.latitude, marker.longitude], zoom);
    lastViewKeyRef.current = nextViewKey;
  }, [resolvedMarkers, zoom]);

  return (
    <div className={styles.container}>
      <div className={styles.mapWrapper}>
        <MapContainer
          center={mapCenter}
          zoom={zoom}
          style={{ height: customHeight, minHeight: customHeight, width: '100%' }}
        >
          <TileLayer
            attribution={LIGHT_TILE_LAYER.attribution}
            url={LIGHT_TILE_LAYER.url}
            subdomains={LIGHT_TILE_LAYER.subdomains}
            maxZoom={LIGHT_TILE_LAYER.maxZoom}
          />
          <MapResizer mapRef={mapRef} visible={visible} />
          {resolvedMarkers.length > 1 && <FitMarkers markers={fitPoints} />}
          {resolvedMarkers.map(marker => (
            <Marker
              key={marker.key}
              position={[marker.latitude, marker.longitude]}
              icon={icons[marker.variant || 'default'] || icons.default}
              draggable={false}
            >
              {marker.label ? <Popup>{marker.label}</Popup> : null}
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default AddressMapViewer;
